import { useGLTF } from "@react-three/drei";
import { useFrame } from "@react-three/fiber";
import { RapierRigidBody, RigidBody } from "@react-three/rapier";
import { useMemo, useRef, useState } from "react";
import * as THREE from "three";
import { Quaternion, Vector3 } from "three";
import { GLTF } from "three-stdlib";
import { useGameStore } from "../../stores/gameStore";

type GLTFResult = GLTF & {
  nodes: {
    Cover: THREE.Mesh;
    Paper: THREE.Mesh;
    Dowel: THREE.Mesh;
  };
  materials: {
    Cover: THREE.MeshStandardMaterial;
    Paper: THREE.MeshStandardMaterial;
    Dowel: THREE.MeshStandardMaterial;
  };
};

export const ScrollStates = {
  PLACED: "PLACED",
  PICKED_UP: "PICKED_UP",
  REMOVED: "REMOVED",
};

export function Scroll(props: JSX.IntrinsicElements["group"]) {
  const { nodes, materials } = useGLTF(
    "/models/assets/scroll.glb"
  ) as GLTFResult;
  const {
    scrollPosition,
    scrollState,
    setScrollState,
    scrollValue,
    setScrollValue,
  } = useGameStore((state: any) => ({
    scrollPosition: state.scrollPosition,
    scrollState: state.scrollState,
    setScrollState: state.setScrollState,
    scrollValue: state.scrollValue,
    setScrollValue: state.setScrollValue,
  }));

  const { memoryLevel, setMemoryLevel } = useGameStore((state: any) => ({
    memoryLevel: state.memoryLevel,
    setMemoryLevel: state.setMemoryLevel,
  }));

  const scrollRef = useRef<RapierRigidBody>(null);
  const [curScrollPosition, setCurScrollPosition] = useState(new Vector3());

  // Ref for rotation angle
  const rotationAngle = useRef(0);
  const quaternionRotation = useMemo(() => new Quaternion(), []);
  const yRotationAxies = new Vector3(0, 1, 0);
  let time = null;

  useFrame((state) => {
    // Update the position of the scroll
    if (
      scrollPosition[0] !== curScrollPosition &&
      scrollPosition[0] &&
      scrollRef.current
    ) {
      scrollRef.current.setTranslation(
        new Vector3(
          scrollPosition[0].x,
          scrollPosition[0].y,
          scrollPosition[0].z
        ),
        true
      );
      setCurScrollPosition(scrollPosition[0]);
    }

    if (scrollState === ScrollStates.PLACED) {
      time = state.clock.elapsedTime / 2;

      // Increment rotation angle
      rotationAngle.current += 0.01;

      // Rotate platform
      scrollRef.current?.setNextKinematicRotation(
        quaternionRotation.setFromAxisAngle(yRotationAxies, time * 0.5)
      );
    }
  });

  const PickUp = () => {
    setMemoryLevel(memoryLevel + scrollValue);
    scrollPosition.splice(0, scrollPosition.length);
    setScrollValue(0);
    setScrollState(ScrollStates.REMOVED);
  };

  return (
    <RigidBody
      name={"Scroll"}
      type={"kinematicPosition"}
      position={curScrollPosition}
      ref={scrollRef}
      onCollisionEnter={({ manifold, target, other }) => {
        if (other.rigidBodyObject!.name === "Sol") {
          setScrollState(ScrollStates.PICKED_UP);
          PickUp();
        }
      }}
    >
      <group {...props} dispose={null}>
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.Cover.geometry}
          material={materials.Cover}
          position={[0, 0.363, 0]}
          rotation={[0, 0, -Math.PI / 2]}
          scale={[0.091, 0.103, 0.091]}
        />
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.Paper.geometry}
          material={materials.Paper}
          position={[0, 0.363, 0]}
          rotation={[0, 0, -Math.PI / 2]}
          scale={[0.091, 0.103, 0.091]}
        />
        <mesh
          castShadow
          receiveShadow
          geometry={nodes.Dowel.geometry}
          material={materials.Dowel}
          position={[0, -0.558, -0.021]}
          rotation={[0.225, 0, 1.563]}
          scale={[0.018, 0.363, 0.018]}
        />
      </group>
    </RigidBody>
  );
}

useGLTF.preload("/models/assets/scroll.glb");
